import React from 'react';
import { TextField, NumberField, SelectField, ToggleField, InfoBox } from '../orizon-ui/FormField';

// ─── Conversioni bozza <-> attributi ──────────────────────────────────────────
// La "bozza" contiene solo stringhe (e booleani) come negli input; gli "attributi"
// sono il jsonb salvato nel database (numeri veri, booleani, stringhe).

/** attributi (jsonb) -> bozza per gli input */
export const bozzaDaAttributi = (campi, attributi) => {
  const bozza = {};
  (campi || []).forEach((c) => {
    const v = attributi?.[c.key];
    if (v === undefined || v === null) return;
    bozza[c.key] = c.tipo === 'booleano' ? Boolean(v) : String(v);
  });
  return bozza;
};

/**
 * bozza -> { attributi, errori }
 * Valida in client i campi obbligatori e i formati; il server (trigger validate_attributi)
 * rimane l'ultima autorità. I campi disattivati mantengono il valore originale.
 * errori è indicizzato come `attributi.<key>`.
 */
export const attributiDaBozza = (campi, bozza, originali = {}) => {
  const attributi = {};
  const errori = {};

  (campi || []).forEach((c) => {
    if (!c.attivo) {
      const o = originali?.[c.key];
      if (o !== undefined && o !== null) attributi[c.key] = o;
      return;
    }

    const raw = bozza?.[c.key];
    const vuoto = raw === undefined || raw === null || (typeof raw === 'string' && raw.trim() === '');

    if (vuoto) {
      if (c.obbligatorio) errori[`attributi.${c.key}`] = 'Obbligatorio';
      return;
    }

    switch (c.tipo) {
      case 'numero': {
        const n = Number(String(raw).replace(',', '.'));
        if (Number.isFinite(n)) attributi[c.key] = n;
        else errori[`attributi.${c.key}`] = 'Numero non valido';
        break;
      }
      case 'booleano':
        attributi[c.key] = Boolean(raw);
        break;
      case 'data':
        if (/^\d{4}-\d{2}-\d{2}$/.test(String(raw))) attributi[c.key] = String(raw);
        else errori[`attributi.${c.key}`] = 'Data non valida';
        break;
      default:
        attributi[c.key] = String(raw).trim();
    }
  });

  return { attributi, errori };
};

const etichettaCampo = (c) => c.label;

/**
 * Disegna i campi dinamici di un reparto (da gru_campi).
 * campi: righe di gru_campi; bozza: stato degli input; onChange(nuovaBozza);
 * errori: oggetto con chiavi `attributi.<key>` (come da attributiDaBozza / mapGruError).
 */
const CampiDinamici = ({ campi, bozza, onChange, errori = {}, disabilitato = false }) => {
  const imposta = (key, valore) => onChange({ ...bozza, [key]: valore });

  const ordinati = [...(campi || [])].sort((a, b) => (a.ordine - b.ordine) || a.label.localeCompare(b.label));
  const visibili = ordinati.filter((c) => c.attivo || (bozza?.[c.key] !== undefined && bozza?.[c.key] !== ''));

  if (visibili.length === 0) {
    return <InfoBox>Questo reparto non ha ancora campi tecnici configurati.</InfoBox>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {visibili.map((c) => {
        const errore = errori[`attributi.${c.key}`];
        const valore = bozza?.[c.key];
        const nome = `attributi.${c.key}`;

        // campo disattivato che ha già un valore: solo lettura
        if (!c.attivo) {
          let mostrato = valore;
          if (c.tipo === 'booleano') mostrato = valore ? 'Sì' : 'No';
          if (c.tipo === 'selezione') {
            mostrato = (c.opzioni || []).find((o) => o.key === valore)?.label ?? valore;
          }
          return (
            <TextField
              key={c.key}
              label={etichettaCampo(c)}
              name={nome}
              value={String(mostrato ?? '')}
              onChange={() => {}}
              disabilitato
              sublabel="campo disattivato"
            />
          );
        }

        switch (c.tipo) {
          case 'numero':
            return (
              <NumberField
                key={c.key}
                label={etichettaCampo(c)}
                name={nome}
                value={valore}
                onChange={(v) => imposta(c.key, v)}
                suffisso={c.unita || undefined}
                obbligatorio={c.obbligatorio}
                errore={errore}
                disabilitato={disabilitato}
              />
            );
          case 'data':
            return (
              <TextField
                key={c.key}
                label={etichettaCampo(c)}
                name={nome}
                type="date"
                value={valore}
                onChange={(v) => imposta(c.key, v)}
                obbligatorio={c.obbligatorio}
                errore={errore}
                disabilitato={disabilitato}
              />
            );
          case 'booleano':
            return (
              <ToggleField
                key={c.key}
                label={etichettaCampo(c)}
                sublabel={c.obbligatorio ? 'obbligatorio' : undefined}
                checked={valore === true}
                onChange={(v) => imposta(c.key, v)}
                disabilitato={disabilitato}
              />
            );
          case 'selezione':
            return (
              <SelectField
                key={c.key}
                label={etichettaCampo(c)}
                name={nome}
                value={valore}
                onChange={(v) => imposta(c.key, v)}
                opzioni={(c.opzioni || []).map((o) => ({ value: o.key, label: o.label }))}
                obbligatorio={c.obbligatorio}
                errore={errore}
                disabilitato={disabilitato}
              />
            );
          default:
            return (
              <TextField
                key={c.key}
                label={etichettaCampo(c)}
                name={nome}
                value={valore}
                onChange={(v) => imposta(c.key, v)}
                obbligatorio={c.obbligatorio}
                errore={errore}
                disabilitato={disabilitato}
              />
            );
        }
      })}
    </div>
  );
};

export default CampiDinamici;
